<template>
    <div
        class="image"
        :class="{ dragHover }"
        v-on:drop="onDrop"
        v-on:dragover="onDragOver"
        v-on:dragleave="onDragLeave"
    >
        <img
            v-if="imageUrl && !uploadCancellationToken"
            :src="imageUrl"
            class="mb-3 rounded-md shadow-md"
            :class="imgClass"
        />

        <br v-if="imageUrl" />

        <label
            class="relative flex h-32 p-0 text-sm text-center text-gray-400 transition border-2 border-gray-300 border-dashed rounded-md cursor-pointer dropzone bg-gray-50 hover:bg-gray-100 hover:text-gray-500"
            ref="dropzone"
            v-show="!imageUrl || uploadCancellationToken"
            @click="uploadCancellationToken ? cancelUpload($event) : null"
        >
            <div class="px-2 m-auto">
                <template v-if="uploadCancellationToken && uploadPercentage < 100">
                    <div class="mb-2 bg-gray-200 rounded">
                        <div class="h-2 bg-green-500 rounded" :style="{ width: uploadPercentage + '%' }"></div>
                    </div>

                    Bezig met uploaden...<br />
                    <span class="text-xs text-gray-400">Klik om te annuleren.</span>
                </template>

                <template v-else-if="uploadCancellationToken && uploadPercentage == 100">
                    <i class="text-2xl fa fa-circle-notch fa-spin"></i>
                    <div class="mt-2">
                        Bezig met optimaliseren...<br />
                        Klik om te annuleren.
                    </div>
                </template>

                <template v-else>
                    <svg
                        class="w-12 h-12 mx-auto text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div class="mt-2 text-gray-400">
                        Sleep hier uw afbeelding<span v-if="multiple">(en)</span> of klik op dit vak
                    </div>
                </template>
            </div>

            <input
                class="absolute inset-0 opacity-0 zIndexBackground"
                type="file"
                dusk="image-upload"
                @change="onChange"
                accept="image/*"
                :required="checkRequired()"
            />
        </label>

        <button
            v-if="!uploadCancellationToken && imageUrl"
            class="mt-1 mb-2 btn btn-red btn-sm"
            type="button"
            dusk="delete"
            @click="removeImage"
        >
            <i class="mr-1 fa fa-trash"></i> Verwijder
        </button>

        <div
            class="explanation fixed bottom-0 left-5 top-1/2 z-[9999] h-[calc(70vh_+_51px)] w-[25vw] -translate-y-1/2 transform overflow-y-auto rounded bg-white p-4 px-7 py-6"
            style="z-index: 999999; width: calc(25vw - 15px)"
            v-if="openExplanation && this.outputInstruction"
        >
            <div v-html="this.outputInstruction"></div>
        </div>

        <avatar-cropper
            v-if="dropzoneRefPresent && hasCrop"
            ref="avatarCropper"
            :class="openExplanation && this.outputInstruction ? 'explanation-open' : ''"
            :uploadHandler="cropUploadHandler"
            :labels="{
                submit: 'Gereed',
                cancel: 'Annuleer',
            }"
            :trigger="$refs['dropzone']"
            :outputOptions="outputOptionsObject"
            :cropperOptions="cropperOptions"
            :mimes="mimeTypes"
            @changed="checkFileForCropping"
            @cancel="openExplanation = false"
            :style="
                '--cropper-box-outline-margin: ' +
                (this.outputOptionsObject.cropperBoxOutline || '') +
                ';' +
                '--cropper-box-outline-margin-show: ' +
                (this.outputOptionsObject.cropperBoxOutline ? 'block' : 'none') +
                ';'
            "
        />

        <input ref="input" type="hidden" :name="inputName" />
    </div>
</template>

<script>
import axios from '@galaxy/modules/Core/resources/js/axiosBuilder';
import AvatarCropper from 'vue-avatar-cropper';

export default {
    components: {
        AvatarCropper,
    },

    props: {
        inputName: String,
        multiple: Boolean | String,
        existingMediaId: String,
        existingMediaUrl: String,
        initialValue: String | Number,
        outputOptions: Object | String,
        required: Boolean | String,
        imgClass: String,
        outputInstruction: String,
    },

    data() {
        return {
            imageUrl: null,
            name: String,
            mounted: false,
            dropzoneRefPresent: false,
            uploadCancellationToken: false,
            uploadPercentage: null,
            dragHover: false,
            mimeTypes:
                'image/png, image/gif, image/jpeg, image/bmp, image/x-icon, image/svg, image/svg+xml, image/webp',
            isRequired: this.required,

            openExplanation: false,
        };
    },

    mounted() {
        this.initInitialValue();
        this.initImageUrl();
        this.initRequestedMimeTypes();

        // @ugly Otherwise avatar croppen element wont find dropzone ref.
        this.dropzoneRefPresent = true;
    },

    methods: {
        initInitialValue() {
            // Set input value.
            this.$refs['input'].value = this.initialValue;
        },

        initImageUrl() {
            // Abort due to no initial value?
            if (!this.initialValue) {
                return;
            }

            // Check if media exists, or if it's a temp file
            if (this.existingMediaId && !isNaN(this.initialValue)) {
                this.imageUrl = this.existingMediaUrl;
            } else {
                this.imageUrl = '/galaxy/crud/admin/form/media/temp?filename=' + encodeURIComponent(this.initialValue);
            }
        },

        onDragOver(event) {
            event.preventDefault();

            this.dragHover = true;
        },

        onDragLeave() {
            this.dragHover = false;
        },

        async onDrop(event) {
            // Stop event.
            event.stopPropagation();
            event.preventDefault();

            this.dragHover = false;

            // Get file.
            let file = _.first(event.dataTransfer?.files || []);

            if (!file) return;

            // Delegate
            if (this.hasCrop && this.isCroppableFile(file)) await this.onCropNewFile(file);
            else await this.onFile(file);
        },

        async onCropNewFile(file) {
            let avatarCropper = this.$refs['avatarCropper'];

            // @note Taken from avatar cropper itself.
            let reader = new FileReader();
            reader.onload = (e) => {
                avatarCropper.dataUrl = e.target.result;
            };
            reader.readAsDataURL(file);

            avatarCropper.filename = file.name || 'unknown';
            avatarCropper.mimeType = avatarCropper.mimeType || file.type;
            avatarCropper.$emit('changed', file, reader);
        },

        async onChange(event) {
            await this.upload(event.target.files[0]);
        },

        async onFile(file) {
            await this.upload(file);
        },

        async upload(file) {
            // Abort due to no file selected?
            if (!file || !file.type) return;

            // Notify user due to not an image?
            if (!file.type.match('image.*')) {
                // Notify
                await Swal.fire({
                    title: 'Ongeldig bestand',
                    text: 'Dit bestand is geen afbeelding. Enkel afbeeldingen zijn toegestaan!',
                    icon: 'warning',
                });

                // Abort
                return;
            }

            if (this.validateRequestedMimeType(file.type)) {
                return;
            }

            await this.uploadToServer(file, file.name);

            // Show
            this.imageUrl = (window.URL || window.webkitURL).createObjectURL(file);
        },

        async cropUploadHandler(resp) {
            let dataType = _.split(resp.originalUrl, ';')[0];
            let mimeType = _.split(dataType, ':')[1];

            if (this.validateRequestedMimeType(mimeType)) {
                return;
            }

            // Crop
            this.$refs['avatarCropper'].cropper.getCroppedCanvas(this.outputOptionsObject).toBlob(async (blob) => {
                // Set image url.
                this.imageUrl = (window.URL || window.webkitURL).createObjectURL(blob);

                // Refresh due to uploaded successfully?
                await this.uploadToServer(blob, this.$refs['avatarCropper'].filename);

                this.openExplanation = false;
            }, mimeType || 'image/jpeg');
        },

        async uploadToServer(file, filename) {
            if (this.validateRequestedMimeType(file.type)) {
                return;
            }

            // Init form data.
            let formData = new FormData();

            formData.append('file', file);
            formData.append('filename', filename);

            // Init cancellation token.
            this.uploadCancellationToken = axios.CancelToken.source();

            // Reset upload percentage.
            this.uploadPercentage = 0;

            try {
                // Request
                let response = await axios.post(
                    '/galaxy/crud/' +
                        ($('html').data('prefix') !== undefined ? $('html').data('prefix') : 'admin/') +
                        'form/media/upload',
                    formData,
                    {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                        cancelToken: this.uploadCancellationToken.token,
                        onUploadProgress: (progressEvent) => {
                            // Calculate and set upload percentage.
                            this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        },
                    }
                );

                // Set tmp file.
                this.$refs['input'].value = response.data.tmpFile;

                return true;
            } catch (e) {
                // Is cancel?
                if (axios.isCancel(e)) return false;

                // Notify user.
                this.$toasted.show(
                    '<i class="far fa-exclamation-triangle"></i> Fout bij verwerken afbeelding. Probeer het (later) opnieuw.',
                    { type: 'error' }
                );

                throw e;
            } finally {
                // Mark as done.
                this.uploadCancellationToken = null;
                this.uploadPercentage = null;
            }
        },

        cancelUpload(event) {
            this.openExplanation = false;

            // Stop event to prevent opening file chooser.
            event.stopPropagation();
            event.preventDefault();

            // Cancel
            this.uploadCancellationToken.cancel();
            this.uploadCancellationToken = null;
        },

        async removeImage() {
            // Remove media.
            this.imageUrl = null;

            this.$refs['input'].value = null;
        },

        checkFileForCropping(file) {
            // Check if file is croppable
            if (!this.isCroppableFile(file)) {
                // Stop avatarCropper from opening
                this.$refs['avatarCropper'].$destroy();

                // Continue handling file
                this.onFile(file);
            } else {
                this.openExplanation = true;

                let interval = setInterval(() => {
                    let button = $(this.$refs['avatarCropper'].$el).find('.avatar-cropper-footer > :last-child');
                    if (button.length) {
                        button.focus();
                        clearInterval(interval);
                    }
                }, 100);
            }
        },

        isCroppableFile(file) {
            return _.includes(['image/png', 'image/jpg', 'image/jpeg', 'image/webp'], file.type);
        },

        initRequestedMimeTypes() {
            if (this.outputOptionsObject?.mime) {
                this.mimeTypes = _.isString(this.outputOptionsObject.mime)
                    ? this.outputOptionsObject.mime
                    : this.outputOptionsObject.mime.join(', ');
            }
        },

        validateRequestedMimeType(givenType) {
            let mimeTypes = null;

            _.isString(this.outputOptionsObject?.mime)
                ? (mimeTypes = [this.outputOptionsObject?.mime])
                : (mimeTypes = this.outputOptionsObject?.mime);

            if (mimeTypes && !mimeTypes.includes(givenType)) {
                // Notify
                Swal.fire({
                    title: 'Ongeldig bestand',
                    text: 'Dit bestand heeft niet het gevraagde bestandstype. Enkel ' + mimeTypes.join(', ') + ' zijn toegestaan!',
                    icon: 'warning',
                });

                // Abort
                return true;
            }

            return false;
        },

        checkRequired() {
            if (this.imageUrl) {
                return false;
            }

            return !!this.isRequired;
        },
    },

    computed: {
        // Not reactive.
        hasCrop() {
            return Object.keys(this.outputOptionsObject).length ? true : false;
        },

        cropperOptions() {
            return {
                aspectRatio: this.outputOptionsObject.aspectRatio || this.outputOptionsObject.width / this.outputOptionsObject.height,
                autoCropArea: 1,
                viewMode: this.outputOptionsObject.viewMode || 1,
                movable: this.outputOptionsObject.moveable || false,
                zoomable: true,
                ...this.outputOptionsObject,
            };
        },

        outputOptionsObject() {
            if (typeof this.outputOptions === 'object') return this.outputOptions;
            else return JSON.parse(this.outputOptions ? this.outputOptions : '{}');
        },
    },
};
</script>

<style lang="scss" scoped>
// TODO Remove the use of Tailwind in the template, since it is not loaded.

.image {
    font-size: 0;
    transition: opacity 0.2s;

    &.dragHover {
        opacity: 0.5;
    }

    img {
        max-height: 300px;
        max-width: 500px;
    }

    .dropzone {
        height: 150px;
    }

    .zIndexBackground {
        z-index: -1;
    }

    &::v-deep {
        .avatar-cropper {
            .avatar-cropper-mark {
                background: rgba(0, 0, 0, 0.3);
                backdrop-filter: blur(3px);
            }

            .avatar-cropper-container {
                @apply transform transition;
            }

            &.explanation-open .avatar-cropper-container {
                @apply translate-x-[calc(15vw_-_30px)];
            }
        }

        .cropper-center {
            &:before {
                width: 14px;
                left: -6px;
            }

            &:after {
                height: 14px;
                top: -6px;
            }
        }

        .avatar-cropper-image-container {
            height: 100%;
            max-height: 70vh !important;
            max-width: 70vw !important;
            width: 100%;

            .cropper-crop-box:before {
                content: '';
                position: absolute;
                inset: 0;
                margin: var(--cropper-box-outline-margin);
                border: 1px solid cyan;
                z-index: 1;
                display: var(--cropper-box-outline-margin-show);
                pointer-events: none;
            }
        }

        .avatar-cropper-btn {
            font-size: 14px;

            &:focus {
                &:last-child {
                    background: #55ce63 !important;
                    color: white;
                }
            }

            &:hover {
                &:first-child {
                    background: #009efb !important;
                }

                &:last-child {
                    background: #55ce63 !important;
                }
            }
        }
    }
}
</style>
